/**
 * ChartCard: title, chart/table toggle, legend, the chart's state in words, and dispatch on `chart.kind`. Clicking a
 * mark drills `chart.drill_metric` with {x, series}. Every chart has a table view (the light palette has three slots
 * under 3:1 on white, so the table and direct labels are mandatory). Owner: slice web-charts (#1115).
 */
import { cn } from '@patchbay/ui'
import * as React from 'react'
import type { Chart, Drill } from '../../../lib/api/generated'
import { BarChart, Histogram, StackedBarChart } from './bar-chart'
import { DASH, formatValue, type Visibility, xLabel } from './format'
import { HBarChart } from './hbar-chart'
import { Heatmap } from './heatmap'
import type { OnDrill } from './kpi'
import { AreaChart, LineChart } from './line-chart'
import { buildModel, type ChartProps, chartHidden, type PlotModel, type PointClick } from './model'
import { StateText } from './state-text'

const FORMS: Record<Chart['kind'], (p: ChartProps) => React.ReactElement | null> = {
  line: LineChart,
  area: AreaChart,
  bar: BarChart,
  stacked_bar: StackedBarChart,
  hbar: HBarChart,
  histogram: Histogram,
  heatmap: Heatmap,
}

export function ChartView({ chart, onPointClick, canSee }: ChartProps) {
  const Form = FORMS[chart.kind]
  return <Form chart={chart} onPointClick={onPointClick} canSee={canSee} />
}

export function ChartCard({
  chart,
  onDrill,
  drillReport,
  wide = false,
  canSee,
}: {
  chart: Chart
  onDrill?: OnDrill
  drillReport: string
  wide?: boolean
  canSee?: (v: Visibility) => boolean
}) {
  const [view, setView] = React.useState<'chart' | 'table'>('chart')
  const model = React.useMemo(() => buildModel(chart, canSee), [chart, canSee])
  const id = React.useId()
  if (chartHidden(chart, canSee)) return null

  const drillMetric = chart.drill_metric
  const onPointClick: PointClick | undefined =
    drillMetric && onDrill
      ? ({ x, series }) => onDrill({ report: drillReport as Drill['report'], metric: drillMetric, grain: 'case', params: { x, series } })
      : undefined
  const state = chart.state ?? 'ok'
  const showLegend = chart.kind !== 'heatmap' && model.plotted.length + model.missing.length >= 2
  const lineKey = chart.kind === 'line'

  return (
    <section
      className={cn('reports-root flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-card p-3', wide && 'md:col-span-2')}
      data-chart={chart.key}
      data-wide={wide ? 'true' : undefined}
      aria-labelledby={`${id}-title`}
    >
      <header className="flex items-start justify-between gap-2">
        <h3 id={`${id}-title`} className="text-sm font-semibold text-foreground">
          {chart.title}
        </h3>
        {state === 'ok' && model.hasData ? (
          <fieldset className="flex shrink-0 rounded-md border border-border p-0.5 text-xs" aria-label={`${chart.title} view`}>
            {(['chart', 'table'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                onClick={() => setView(v)}
                className={cn(
                  'rounded px-2 py-0.5 capitalize',
                  view === v ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {v}
              </button>
            ))}
          </fieldset>
        ) : null}
      </header>
      {state !== 'ok' ? (
        <div className="flex min-h-24 items-center justify-center py-6">
          <StateText state={state} note={chart.note} />
        </div>
      ) : !model.hasData ? (
        <div className="flex min-h-24 flex-col items-center justify-center gap-1 py-6">
          {model.missing.length > 0 ? (
            model.missing.map((s) => (
              <span key={s.key} className="text-sm text-muted-foreground">
                {s.label}: <StateText state={s.state ?? 'unavailable'} />
              </span>
            ))
          ) : (
            <span className="text-sm text-muted-foreground">Nothing matched in this range.</span>
          )}
        </div>
      ) : (
        <>
          {showLegend ? (
            <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" data-legend>
              {model.plotted.map((p) => (
                <li key={p.key} className="flex items-center gap-1.5">
                  <span
                    aria-hidden
                    className={cn('inline-block shrink-0', lineKey ? 'h-0.5 w-3 rounded-full' : 'size-2.5 rounded-sm')}
                    style={{ background: p.color }}
                  />
                  {p.label}
                </li>
              ))}
              {model.missing.map((s) => (
                <li key={s.key} className="flex items-center gap-1.5">
                  <span aria-hidden className="inline-block size-2.5 rounded-sm border border-dashed border-border-strong" />
                  {s.label}
                  <span className="text-muted-foreground">
                    ({s.state === 'insufficient' ? 'too few cases' : s.state === 'not_tracked' ? 'not tracked yet' : "couldn't load"})
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
          {view === 'chart' ? (
            <ChartView chart={chart} onPointClick={onPointClick} canSee={canSee} />
          ) : (
            <ChartTable chart={chart} model={model} onPointClick={onPointClick} />
          )}
        </>
      )}
      {state === 'ok' && chart.note ? <p className="text-xs text-muted-foreground">{chart.note}</p> : null}
    </section>
  )
}

/** The table view of a chart: one row per x (per series for a heatmap), every value in words, drillable. */
export function ChartTable({ chart, model, onPointClick }: { chart: Chart; model: PlotModel; onPointClick?: PointClick }) {
  const heat = chart.kind === 'heatmap'
  const head = heat ? model.xs.map((x) => xLabel(chart, x)) : model.plotted.map((p) => p.label)
  const rowKeys = heat ? model.plotted.map((p) => p.key) : model.xs
  const colKeys = heat ? model.xs : model.plotted.map((p) => p.key)
  const value = (r: number, c: number) => {
    const s = heat ? model.plotted[r] : model.plotted[c]
    const i = heat ? c : r
    return { series: s, x: model.xs[i], v: s ? (s.values[i] ?? null) : null, n: s ? (s.ns[i] ?? null) : null }
  }
  return (
    <div className="overflow-x-auto" data-chart-table>
      <table className="w-full border-collapse text-sm">
        <thead className="bg-muted text-xs text-muted-foreground">
          <tr>
            <th scope="col" className="px-2 py-1 text-left font-medium">
              {heat ? '' : chart.x_kind === 'date' ? 'Period' : 'Category'}
            </th>
            {head.map((h, k) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: header labels can repeat
              <th key={k} scope="col" className="px-2 py-1 text-right font-medium whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((rk, r) => (
            <tr key={rk} className="border-t border-border">
              <th scope="row" className="px-2 py-1 text-left font-normal whitespace-nowrap text-foreground">
                {heat ? model.plotted[r]?.label : xLabel(chart, rk, true)}
              </th>
              {colKeys.map((ck, c) => {
                const { series, x, v, n } = value(r, c)
                const text = v === null ? 'no data' : formatValue(chart.unit, v)
                return (
                  <td
                    key={ck}
                    className={cn(
                      'tabular px-2 py-1 text-right whitespace-nowrap',
                      v === null ? 'text-muted-foreground' : 'text-foreground',
                    )}
                  >
                    {v !== null && onPointClick && series && x !== undefined ? (
                      <button type="button" className="hover:underline" onClick={() => onPointClick({ x, series: series.key })}>
                        {text}
                      </button>
                    ) : (
                      text
                    )}
                    {n ? <span className="ml-1 text-xs text-muted-foreground">n={n}</span> : null}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {model.plotted.length === 0 ? <p className="py-2 text-sm text-muted-foreground">{DASH}</p> : null}
    </div>
  )
}
